import type { AxiosInstance } from "axios";

import { parseAxiosResponse } from "@thalamos/common";
import type {
  AuditTrailEntryKind,
  AuditTrailPage,
  UserId,
} from "@thalamos/haven-common";
import { GetAuditTrailResponseSchema } from "@thalamos/haven-common";

import type { ErrorBody, ResponseBody } from "../axios/api.js";
import { useApiAxios } from "../axios/api.js";

const parseResponseTrail = parseAxiosResponse(GetAuditTrailResponseSchema);

/**
 * A search of the trail, as a caller states it.
 *
 * The window is required, because the endpoint requires it: a caller should say
 * which period it is asking about rather than discovering a default the hard way.
 * Everything else narrows.
 */
export type TrailSearch = {
  fromDate: string;
  toDate: string;
  kinds?: AuditTrailEntryKind[];
  userId?: UserId;
  resourceId?: string;
  limit?: number;
  offset?: number;
};

/**
 * Only the narrowing a caller actually named is sent. An empty `kinds` is not a
 * request for no kinds — it is the absence of a kind filter — so it is dropped
 * rather than sent as an empty parameter the server would have to interpret.
 */
const trailParams = ({
  fromDate,
  toDate,
  kinds,
  userId,
  resourceId,
  limit,
  offset,
}: TrailSearch) => ({
  fromDate,
  toDate,
  // Comma-separated, which is how the endpoint takes them.
  ...(kinds !== undefined && kinds.length > 0
    ? { kinds: kinds.join(",") }
    : {}),
  ...(userId !== undefined ? { userId } : {}),
  ...(resourceId !== undefined && resourceId.length > 0 ? { resourceId } : {}),
  ...(limit !== undefined ? { limit } : {}),
  ...(offset !== undefined ? { offset } : {}),
});

export const config = {
  auditTrail: {
    search: (search: TrailSearch) => ({
      url: "/api/audit-trail",
      method: "get",
      params: trailParams(search),
      transformResponse: parseResponseTrail,
    }),
  },
};

export const api = (apiAxios: AxiosInstance) => {
  return {
    auditTrail: {
      search: (search: TrailSearch) =>
        apiAxios.request<ResponseBody<AuditTrailPage, ErrorBody>>(
          config.auditTrail.search(search),
        ),
    },
  };
};

export const apiHooks = () => {
  return {
    auditTrail: {
      search: function useAuditTrailSearch(search: TrailSearch) {
        return useApiAxios<AuditTrailPage>(config.auditTrail.search(search));
      },
    },
  };
};
